import { chain } from 'lodash';
import {
  Dispatch,
  default as React,
  ReactElement,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { DomainTypeGq } from '../../../../graphql/projects/models/DomainTypeGq';
import { GoogleAdsRequestGq } from '../../../../graphql/projects/models/GoogleAdsRequestGq';
import { ProjectGq } from '../../../../graphql/projects/models/ProjectGq';
import { ProjectKeywordGq } from '../../../../graphql/projects/models/ProjectKeywordGq';
import { QueuedItemStatus } from '../../../../graphql/projects/models/QueuedItemStatus';
import { TagGq } from '../../../../graphql/projects/models/TagGq';
import { TagGroupGq } from '../../../../graphql/projects/models/TagGroupGq';
import { GetSelectedKeywordsStatsGq } from '../../../../graphql/projects/queries/useGetSelectedKeywordsStatsGq';
import { CurrentUserGq } from '../../../../graphql/users/models/CurrentUserGq';
import VoxelDataTable from '../../../voxel/DataTable/VoxelDataTable';
import VoxelDataTableBody from '../../../voxel/DataTable/VoxelDataTableBody';
import SearchCommandsInfo from '../../../voxel/Search/SearchCommandsInfo';
import VoxelAdvancedTextSearch from '../../../voxel/Search/VoxelAdvancedTextSearch';
import VoxelPanel from '../../../voxel/Surfaces/VoxelPanel';
import ArchiveCheckbox from '../../Checkbox/ArchiveCheckbox';
import { FastTagGroup } from '../Shared/TableCellTagGroups';
import HeadRow from './HeadRow';
import { getColumnDefaultSize } from './KeywordTableColumnPicker/getColumnDefaultSize';
import { getColumnId } from './KeywordTableColumnPicker/getColumnId';
import {
  KeywordTableColumnSize,
  KeywordTableColumnTypes,
  monthNames,
} from './KeywordTableColumnPicker/models';
import KeywordTablePagination from './KeywordTablePagination';
import KeywordTableRow from './KeywordTableRow';
import KeywordTableToolbar from './KeywordTableToolbar';
import { useDragBag } from './logic/useDragBag';
import { KeywordTableState } from './logic/useKeywordTableState';
import VoxelDataTableHead from '../../../voxel/DataTable/VoxelDataTableHead';
import moment from 'moment';

export interface Props {
  project?: ProjectGq;
  requests?: Record<number, GoogleAdsRequestGq>;
  data?: ProjectKeywordGq[];
  loading?: boolean;
  totalCount: number;
  totalVolume: number;
  currentPage: number;
  keywordTableState: KeywordTableState;
  includeExportMonths: boolean;
  reloadKeywords: () => Promise<any>;
  tagGroups?: TagGroupGq[];
  onDownload?: () => void;
  changeVisibleColumns?: boolean;
  selectedKeywordStats?: GetSelectedKeywordsStatsGq;
  isScrolling?: boolean;
  rankCtrValues?: Record<string, number>;
  disabled?: boolean;
  page?: KeywordTablePage;
  lastProgressReqId?: number;
  extraButtons?: ReactElement;
  openRanksDialog?: boolean;
  setOpenRanksDialog?: Dispatch<SetStateAction<boolean>>;
  setOpenAddDomainDialog?: Dispatch<SetStateAction<DomainTypeGq>>;
  openGetRanksDialog?: boolean;
  user?: CurrentUserGq;
  drawerOpen?: boolean;
}
export type DmPage = 'ranks' | 'tags' | 'search';
export type KeywordTablePage = 'dm' | 'progress' | 'quicksearch';

export const KeywordTable: React.FC<Props> = React.memo(
  ({
    project,
    requests,
    totalCount,
    data,
    loading,
    keywordTableState,
    currentPage,
    reloadKeywords,
    tagGroups,
    onDownload,
    changeVisibleColumns,
    selectedKeywordStats,
    isScrolling,
    rankCtrValues,
    lastProgressReqId,
    page,
    extraButtons,
    setOpenRanksDialog,
    setOpenAddDomainDialog,
    openGetRanksDialog,
    user,
    drawerOpen,
  }) => {
    const { filterState, setFilterState } = keywordTableState;
    const dragBag = useDragBag(keywordTableState, data);

    // Fast tag groups
    const fastTagGroups = useMemo(() => {
      const result: Record<number, FastTagGroup> = {};

      project?.tagGroups.forEach((tagGroup) => {
        const tags: Record<number, TagGq> = {};
        tagGroup.tags.forEach((tag) => (tags[tag.id] = tag));
        result[tagGroup.id] = {
          tagGroup,
          tags,
        };
      });

      return result;
    }, [project?.tagGroups]);

    // Keypresses
    useEffect(() => {
      const fn = (event: KeyboardEvent) => {
        if (
          event.shiftKey &&
          event.key === 'ArrowRight' &&
          currentPage !== Math.floor(totalCount / keywordTableState.pagingState.amountPerPage)
        ) {
          const nextPage = currentPage + 1;
          keywordTableState.setPagingState((prev) => ({ ...prev, page: nextPage }));
        }
        if (event.shiftKey && event.key === 'ArrowLeft' && currentPage !== 0) {
          const prevPage = currentPage - 1;
          keywordTableState.setPagingState((prev) => ({ ...prev, page: prevPage }));
        }
      };
      document.addEventListener('keydown', fn);
      return () => {
        document.removeEventListener('keydown', fn);
      };
    }, [keywordTableState, totalCount, currentPage]);

    // Figure out if table is scrolled outside of view
    const [tableContainerRef, setTableContainerRef] = useState<HTMLDivElement>(null);

    // Tracking requests
    const trackingRequests = useMemo(
      () =>
        chain(project?.projectSerpTrackingRequests ?? [])
          .filter(
            (pr) =>
              pr.status === QueuedItemStatus.SUCCEEDED &&
              (!keywordTableState.backendFilterState.progressRequestIds?.length ||
                keywordTableState.backendFilterState.progressRequestIds.includes(pr.id))
          )
          .orderBy((tr) => tr.scheduledTime)
          .value(),
      [
        project?.projectSerpTrackingRequests,
        keywordTableState?.backendFilterState?.progressRequestIds,
      ]
    );

    const hasInvalidRegex = useMemo(() => {
      const searchTerm = keywordTableState?.filterState?.keyword?.searchTerm;
      if (!searchTerm?.length) return false;
      if (!searchTerm.includes('^') || !searchTerm.includes('$')) return false;
      try {
        new RegExp(searchTerm);
        return false;
      } catch (e) {
        return true;
      }
    }, [keywordTableState?.filterState?.keyword?.searchTerm]);

    useEffect(() => {
      const handler = (event: any) => {
        if (event?.relatedTarget?.nodeName === 'HTML') {
          dragBag.deactivateDragMode && dragBag.deactivateDragMode(undefined);
        }
      };
      document.addEventListener('mouseout', handler);
      return () => document.removeEventListener('mouseout', handler);
    }, [dragBag.deactivateDragMode]);

    // Optimization for applying tags and forwarding volatile state which might break memoization otherwise
    const [openKeywordId, setOpenKeywordId] = useState<number>(undefined);

    // Render
    return (
      <div className="h-full">
        <KeywordTableToolbar
          project={project}
          user={user}
          tableState={keywordTableState}
          page={page}
          reloadKeywords={reloadKeywords}
          onDownload={onDownload}
          selectedKeywordStats={selectedKeywordStats}
          isScrolling={isScrolling}
          extraButtons={extraButtons}
          setOpenRanksDialog={setOpenRanksDialog}
          setOpenAddDomainDialog={setOpenAddDomainDialog}
          changeVisibleColumns={changeVisibleColumns}
          openGetRanksDialog={openGetRanksDialog}
        />
        {page !== 'quicksearch' && !project?.progressKeywordCount && !data?.length && (
          <div className={'w-full'}>
            <VoxelPanel>
              <div className="text-voxel-gray-500 p-6">{'Project has no keywords'}</div>
            </VoxelPanel>
          </div>
        )}
        {(!!project?.progressKeywordCount || !!data?.length) && (
          <>
            <div className={`bg-white rounded-lg shadow-sm ${page === 'dm' ? '' : ''}`}>
              <div className=" w-fit p-2 flex flex-row items-center justify-start gap-6">
                <div className="w-56">
                  <VoxelAdvancedTextSearch
                    size="sm"
                    widthClass="w-56"
                    error={hasInvalidRegex}
                    value={keywordTableState.filterState?.keyword?.searchTerm ?? ''}
                    onChange={(event) =>
                      keywordTableState.setFilterState((prev) => ({
                        ...prev,
                        keyword: { searchTerm: event.target.value },
                      }))
                    }
                    placeholder={'Keyword filter'}
                    infoIcon={<SearchCommandsInfo />}
                    deleteValue={() =>
                      keywordTableState.setFilterState((prev) => ({
                        ...prev,
                        keyword: { searchTerm: '' },
                      }))
                    }
                  />
                </div>
                {page !== 'progress' && (
                  <ArchiveCheckbox filterState={filterState} setFilterState={setFilterState} />
                )}
              </div>

              <div
                ref={setTableContainerRef}
                data-scroll-container="true"
                className={`
                 bg-gray-50
                 overflow-auto overscroll-contain
                 ${page === 'dm' && 'max-h-[calc(100vh-56px-40px-54px-40px)]'} 
                 ${page === 'quicksearch' && 'max-h-[calc(100vh-56px-40px-54px-32px)]'} 
                 ${page === 'progress' && 'max-h-[calc(100vh-56px-223.23px-40px-40px)]'} 
                 ${page === 'dm' && drawerOpen && 'max-w-[calc(100vw-10*4px-80*4px)]'}
                 ${page === 'dm' && !drawerOpen && 'max-w-[calc(100vw-10*4px)]'}
                 ${page === 'quicksearch' && 'max-w-[calc(100vw-10*4px-80*4px)]'}
              `}
              >
                <VoxelDataTable className={`table-fixed w-full`}>
                  <colgroup>
                    <col className="w-10" />
                    {keywordTableState.columnPickerBag.shownOrderedColumns.map((column) => {
                      if (!column) return null;
                      if (!(column as any).type && (column as any).type !== 0) return null;

                      const key = getColumnId(column);
                      const size = column.size ?? getColumnDefaultSize(column.type);

                      // Chosen sizes
                      let sizeClass = '';
                      if (size === KeywordTableColumnSize.xs) sizeClass = 'w-24';
                      if (size === KeywordTableColumnSize.sm) sizeClass = 'w-28';
                      if (size === KeywordTableColumnSize.md) sizeClass = 'w-36';
                      if (size === KeywordTableColumnSize.lg) sizeClass = 'w-48';
                      if (size === KeywordTableColumnSize.xl) sizeClass = 'w-64';

                      if (column.type === KeywordTableColumnTypes.VolumeLast12months) {
                        return (
                          <React.Fragment key={key}>
                            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((index) => {
                              let localKey = key + index;
                              return <col key={localKey} className={sizeClass} />;
                            })}
                          </React.Fragment>
                        );
                      }

                      if (column.type === KeywordTableColumnTypes.Rank) {
                        return (
                          <React.Fragment key={key}>
                            <col
                              className={sizeClass}
                              style={!!column.sizePx ? { width: column.sizePx } : undefined}
                            />
                            {page === 'progress' && <col className="w-20" />}
                            {!!column.traffic && <col className="w-28" />}
                            {!!column.traffic && page === 'progress' && <col className="w-24" />}
                            {!!column.url && <col className="w-64" />}
                          </React.Fragment>
                        );
                      }
                      return (
                        <col
                          key={key}
                          className={sizeClass}
                          style={!!column.sizePx ? { width: column.sizePx } : undefined}
                        />
                      );
                    })}
                    <col className="w-auto" />
                  </colgroup>
                  <VoxelDataTableHead>
                    <HeadRow
                      page={page}
                      tableContainerRef={tableContainerRef}
                      project={project}
                      keywordTableState={keywordTableState}
                      requests={requests}
                      shownColumns={keywordTableState.columnPickerBag.shownOrderedColumns}
                      isProgressPage={page === 'progress'}
                    />
                  </VoxelDataTableHead>
                  <VoxelDataTableBody>
                    {!!data?.length &&
                      data.map((keyword, index) => {
                        const clicked = !!keywordTableState.selectionState.keywordIds[keyword.id];
                        const selected = !!keywordTableState.selectionState.invertSelection
                          ? !clicked
                          : clicked;
                        return (
                          <KeywordTableRow
                            key={keyword.id}
                            trackingRequests={trackingRequests}
                            setTableUIState={keywordTableState.setTableUIState}
                            project={project}
                            apiCallCollections={requests}
                            keyword={keyword}
                            tableUIState={keywordTableState.tableUIState}
                            fastTagGroups={fastTagGroups}
                            index={index}
                            dragSelected={dragBag.isIndexSelected(index)}
                            selected={selected}
                            onMouseOver={dragBag.onMouseOver}
                            onMouseDown={dragBag.onMouseDown}
                            onMouseUp={dragBag.deactivateDragMode}
                            shownColumns={keywordTableState.columnPickerBag.shownOrderedColumns}
                            rankCtrValues={rankCtrValues}
                            isProgressPage={page === 'progress'}
                            areVariantsLoading={
                              keywordTableState.variantState.loadingKeywords[keyword.id]
                            }
                            localVariant={keywordTableState.variantState.localVariants[keyword.id]}
                            handleSelectVariant={keywordTableState.handleSelectVariant}
                            reload={reloadKeywords}
                            user={user}
                            setOpenKeywordId={setOpenKeywordId}
                            backendFiltering={
                              openKeywordId === keyword.id
                                ? keywordTableState.backendFilterState
                                : undefined
                            }
                            backendSelection={
                              openKeywordId === keyword.id
                                ? keywordTableState.backendSelectionState
                                : undefined
                            }
                            resetSelection={
                              openKeywordId === keyword.id
                                ? keywordTableState.handleResetSelection
                                : undefined
                            }
                          />
                        );
                      })}
                  </VoxelDataTableBody>
                </VoxelDataTable>
              </div>
            </div>
            <KeywordTablePagination
              setPagingState={keywordTableState.setPagingState}
              pagingState={keywordTableState.pagingState}
              totalCount={totalCount}
              tableRef={tableContainerRef}
              currentPage={currentPage}
              selectedKeywordStats={selectedKeywordStats}
              dataRef={data}
            />
          </>
        )}
      </div>
    );
  }
);
export default KeywordTable;
